import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, TrendingUp, CalendarDays, Search, Download, Eye, Loader2, ArrowLeft, ShieldCheck, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SEOHead } from '@/components/SEOHead';
import { useAdmin } from '@/hooks/useAdmin';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/gaius-logo.webp';
import * as XLSX from 'xlsx';
import { AdminNotificationBell } from '@/components/admin/AdminNotificationBell';

interface ClientRow {
  email: string;
  name: string;
  company: string | null;
  enrollmentCount: number;
  totalSpent: number;
  lastActivity: string;
  paymentStatuses: string[];
  isGuest: boolean;
}

interface RawEnrollment {
  id: string;
  training_title: string;
  training_slug: string;
  modality: string;
  total_price: string;
  payment_status: string;
  created_at: string;
  guest_name: string | null;
  guest_email: string | null;
  user_id: string | null;
}

const statusBadge = (s: string) => {
  switch (s) {
    case 'paid': return <Badge className="bg-emerald-100 text-emerald-800 text-xs">Payé</Badge>;
    case 'refunded': return <Badge className="bg-orange-100 text-orange-800 text-xs">Remboursé</Badge>;
    default: return <Badge className="bg-yellow-100 text-yellow-800 text-xs">En attente</Badge>;
  }
};

export const AdminDashboardPage = () => {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState<RawEnrollment[]>([]);
  const [profiles, setProfiles] = useState<Record<string, { full_name: string | null; company: string | null; email: string }>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFormation, setFilterFormation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!adminLoading && !authLoading && (!user || !isAdmin)) {
      navigate('/');
    }
  }, [isAdmin, adminLoading, authLoading, user, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchData = async () => {
      // Fetch all enrollments
      const { data: enrollData } = await supabase
        .from('enrollments')
        .select('*')
        .order('created_at', { ascending: false });

      if (enrollData) setEnrollments(enrollData as unknown as RawEnrollment[]);

      // Fetch all profiles + auth emails
      const { data: profileData } = await supabase
        .from('profiles')
        .select('user_id, full_name, company');

      if (profileData) {
        const map: Record<string, { full_name: string | null; company: string | null; email: string }> = {};
        for (const p of profileData as any[]) {
          map[p.user_id] = { full_name: p.full_name, company: p.company, email: '' };
        }
        setProfiles(map);
      }

      setLoading(false);
    };
    fetchData();
  }, [isAdmin]);

  // Build client rows by grouping enrollments
  const clients = useMemo(() => {
    const map = new Map<string, ClientRow>();

    for (const e of enrollments) {
      const email = e.guest_email || (e.user_id ? (profiles[e.user_id]?.email || e.user_id) : 'inconnu');
      const key = email.toLowerCase();
      const existing = map.get(key);
      const price = parseFloat(e.total_price.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;

      if (existing) {
        existing.enrollmentCount++;
        existing.totalSpent += price;
        if (e.created_at > existing.lastActivity) existing.lastActivity = e.created_at;
        if (!existing.paymentStatuses.includes(e.payment_status)) existing.paymentStatuses.push(e.payment_status);
      } else {
        const name = e.guest_name || profiles[e.user_id || '']?.full_name || '';
        map.set(key, {
          email: e.guest_email || email,
          name: name || '',
          company: profiles[e.user_id || '']?.company || null,
          enrollmentCount: 1,
          totalSpent: price,
          lastActivity: e.created_at,
          paymentStatuses: [e.payment_status],
          isGuest: !e.user_id,
        });
      }
    }

    return Array.from(map.values());
  }, [enrollments, profiles]);

  // Filtering
  const filteredClients = useMemo(() => {
    let result = clients;
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(c => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s));
    }
    if (filterFormation !== 'all') {
      const matchingEmails = new Set(
        enrollments
          .filter(e => e.training_slug === filterFormation)
          .map(e => (e.guest_email || e.user_id || '').toLowerCase())
      );
      result = result.filter(c => matchingEmails.has(c.email.toLowerCase()));
    }
    if (filterStatus !== 'all') {
      const matchingEmails = new Set(
        enrollments
          .filter(e => e.payment_status === filterStatus)
          .map(e => (e.guest_email || e.user_id || '').toLowerCase())
      );
      result = result.filter(c => matchingEmails.has(c.email.toLowerCase()));
    }
    return result;
  }, [clients, search, filterFormation, filterStatus, enrollments]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnrollments = enrollments.filter(e => new Date(e.created_at) >= monthStart);
    const monthRevenue = monthEnrollments.reduce((sum, e) => {
      return sum + (parseFloat(e.total_price.replace(/[^0-9.,]/g, '').replace(',', '.')) || 0);
    }, 0);
    return {
      totalClients: clients.length,
      monthEnrollments: monthEnrollments.length,
      monthRevenue,
    };
  }, [clients, enrollments]);

  // Unique formations for filter
  const formations = useMemo(() => {
    const set = new Map<string, string>();
    enrollments.forEach(e => set.set(e.training_slug, e.training_title));
    return Array.from(set.entries());
  }, [enrollments]);

  const exportExcel = () => {
    const data = filteredClients.map(c => ({
      Nom: c.name,
      Email: c.email,
      'Type': c.isGuest ? 'Invité' : 'Inscrit',
      'Nb formations': c.enrollmentCount,
      'Total dépensé (€)': c.totalSpent.toFixed(2),
      'Dernière activité': new Date(c.lastActivity).toLocaleDateString('fr-FR'),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, `clients_gaius_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (adminLoading || authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Backoffice Admin — Gaius" description="Gestion des inscriptions et clients" />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={logo} alt="Gaius" className="h-10" />
              </Link>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <div>
                  <h1 className="text-xl font-bold text-foreground">Backoffice Admin</h1>
                  <p className="text-sm text-muted-foreground">Gestion des inscriptions</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AdminNotificationBell />
              <Link to="/admin/emails">
                <Button variant="outline" size="sm">
                  <Mail className="w-4 h-4 mr-1" /> Suivi emails
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Mon espace
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total clients</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalClients}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inscriptions ce mois</CardTitle>
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.monthEnrollments}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Revenus ce mois</CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.monthRevenue.toFixed(0)} €</div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterFormation} onValueChange={setFilterFormation}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Formation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les formations</SelectItem>
              {formations.map(([slug, title]) => (
                <SelectItem key={slug} value={slug}>{title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="paid">Payé</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="refunded">Remboursé</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportExcel}>
            <Download className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-center">Formations</TableHead>
                  <TableHead className="text-right">Total dépensé</TableHead>
                  <TableHead>Dernière activité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Aucun client trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {c.name || '—'}
                          {c.isGuest && <Badge variant="outline" className="text-[10px]">Invité</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{c.email}</TableCell>
                      <TableCell className="text-center">{c.enrollmentCount}</TableCell>
                      <TableCell className="text-right font-medium">{c.totalSpent.toFixed(0)} €</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(c.lastActivity).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {c.paymentStatuses.map((s, j) => <React.Fragment key={j}>{statusBadge(s)}</React.Fragment>)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/admin/client/${encodeURIComponent(c.email)}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Détail
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
